/**
 * GST helper — reads the rate from the ATO Knowledge Base and provides
 * the two shapes of maths tradie surfaces need:
 *
 *   1. `computeGstFromInclusive(total)` — pulls the GST portion out of
 *      a GST-inclusive total (the docket amount tradies enter).
 *   2. `gstAutoRatioLabel()` — human-readable ratio for UI hints
 *      ("1/11th" when the rate is 10%; auto-adapts if the rate changes).
 *
 * SAFETY: `readRate("gst")` returns `undefined` if the rulesets section
 * is not `"confirmed"` (see `src/tax/rulesets/knowledge/index.ts`). In
 * that case we FALL BACK to the historical 10% so screens never break
 * and calculations never quietly return 0.
 *
 * `getGstStamp()` returns the ruleset audit stamp — attach to any tax
 * output for a trail of which ATO ruleset produced the number.
 */

import { readRate, stampRuleset } from "@/tax/rulesets/knowledge";
import type { AtoRulesetStamp } from "@/tax/rulesets/knowledge";

/** Fallback GST rate in PERCENT. Applies since 1 Jul 2000. */
const FALLBACK_GST_PERCENT = 10;

/**
 * Current GST rate as a percentage (e.g. 10 for 10%). Never throws;
 * always returns a numeric value — historical fallback if the KB
 * returns undefined.
 */
export function getGstPercent(): number {
  const rate = readRate("gst");
  const amount = rate?.amount;
  return typeof amount === "number" && amount > 0 ? amount : FALLBACK_GST_PERCENT;
}

/**
 * GST fraction for extraction from a GST-inclusive total.
 *   For 10% GST: 10 / (100 + 10) = 0.0909… (equivalent to 1/11).
 */
export function getGstInclusiveFraction(): number {
  const pct = getGstPercent();
  return pct / (100 + pct);
}

/**
 * Compute the GST portion contained in a GST-inclusive total. Rounds to
 * 2 decimal places to match the existing behaviour of Review/EntryEdit.
 *
 * @example
 *   computeGstFromInclusive(110)  // → 10  (for 10% GST)
 *   computeGstFromInclusive(220)  // → 20
 */
export function computeGstFromInclusive(totalIncl: number): number {
  const t = Number(totalIncl) || 0;
  const g = t * getGstInclusiveFraction();
  return Math.round(g * 100) / 100;
}

/**
 * UI ratio label — "1/11th" when the rate is 10%. Auto-computes for
 * any future rate so screens like Review and EntryEdit don't need to
 * change wording if the ATO ever bumps the rate.
 */
export function gstAutoRatioLabel(): string {
  const pct = getGstPercent();
  // 10% → 1/11; 12.5% → 1/9; etc. Round to nearest integer denominator
  // so the label stays clean even if a future rate produces an ugly
  // decimal fraction.
  const denom = Math.round((100 + pct) / pct);
  return `1/${denom}th`;
}

/**
 * Audit stamp for any tax output that used the GST rate. Attach this
 * to logs / payloads so a future audit can prove which ruleset produced
 * a given number. Also useful for BAS/TPAR/PDF exports.
 */
export function getGstStamp(): AtoRulesetStamp {
  return stampRuleset();
}
